// Time cascade for a show's running order.
//
// Given a show's start time, gap-between-dances, optional intermission, and
// the ordered dances with their lengths, compute each dance's start/end
// time. Pure function — the UI calls this on every drag-and-drop reorder to
// render a live preview; the DB-side `recompute_show_timeline()` does the
// same math when an admin commits the new order.

export interface CascadeDance {
  dance_id: string;
  sequence_index: number; // 1-based
  length_seconds: number;
}

export interface CascadeShow {
  performance_at: string; // ISO datetime with offset
  default_gap_seconds: number;
  intermission_after_sequence: number | null;
  intermission_minutes: number;
}

export interface CascadeResult {
  dance_id: string;
  sequence_index: number;
  computed_start_time: string;
  computed_end_time: string;
}

export function cascadeShowTimeline(show: CascadeShow, dances: CascadeDance[]): CascadeResult[] {
  // Sort defensively — caller may pass any order.
  const sorted = [...dances].sort((a, b) => a.sequence_index - b.sequence_index);

  const start = new Date(show.performance_at);
  const out: CascadeResult[] = [];
  let cursor = start.getTime();

  const gapMs = show.default_gap_seconds * 1000;
  const intermissionMs = show.intermission_minutes * 60 * 1000;
  const interAfter = show.intermission_after_sequence;

  for (const d of sorted) {
    const startTime = new Date(cursor);
    const endTime = new Date(cursor + d.length_seconds * 1000);
    out.push({
      dance_id: d.dance_id,
      sequence_index: d.sequence_index,
      computed_start_time: startTime.toISOString(),
      computed_end_time: endTime.toISOString(),
    });
    cursor = endTime.getTime() + gapMs;
    if (interAfter !== null && d.sequence_index === interAfter) {
      cursor += intermissionMs;
    }
  }

  return out;
}

// Helper used by the UI's drag-and-drop reorder — given a list of dance ids
// in the new order, return a new sequence_index assignment 1..N.
export function reorderLineup<T extends { dance_id: string }>(
  newOrder: T[],
): (T & { sequence_index: number })[] {
  return newOrder.map((d, i) => ({ ...d, sequence_index: i + 1 }));
}
